import { z } from 'zod';
import type { Currency } from './currencies';

/**
 * Money helpers. Numbers come off the DB as `string` because of `numeric()` —
 * always parse them before doing math; never reformat what the DB sends without going through here.
 */

/** Parse a Drizzle `numeric` string into a number. Throws on NaN. */
export function parseMoney(raw: string | number | null | undefined): number {
  if (raw === null || raw === undefined) return 0;
  const n = typeof raw === 'number' ? raw : Number.parseFloat(raw);
  if (Number.isNaN(n)) throw new Error(`Invalid money value: ${raw}`);
  return n;
}

/** Round to the precision the currency uses (EUR → 2dp, CVE → 0dp). */
export function roundMoney(amount: number, currency: Currency): number {
  return currency === 'CVE' ? Math.round(amount) : Math.round(amount * 100) / 100;
}

/** A booking's monetary fields snapshot — always paired with currency to keep totals sane. */
export const moneyAmountSchema = z.object({
  eur: z.number().nonnegative(),
  cve: z.number().nonnegative(),
});
export type MoneyAmount = z.infer<typeof moneyAmountSchema>;

/** Pick the side that matches the customer's currency. */
export function pickMoney(amount: MoneyAmount, currency: Currency): number {
  return currency === 'EUR' ? amount.eur : amount.cve;
}
